"use client";

import * as React from "react";

import { cn } from "@/src/lib/utils";

type AsProp<T extends React.ElementType> = {
  as?: T;
};

type PropsToOmit<T extends React.ElementType, P> = keyof (AsProp<T> & P);

export type PolymorphicComponentProps<
  T extends React.ElementType,
  Props = {},
> = React.PropsWithChildren<Props & AsProp<T>> &
  Omit<React.ComponentPropsWithoutRef<T>, PropsToOmit<T, Props>>;

export type NumericTextProps<T extends React.ElementType = "span"> =
  PolymorphicComponentProps<
    T,
    {
      /**
       * Optional explicit value. If omitted, renders `children`.
       */
      value?: React.ReactNode;
      /**
       * Enables tabular numbers for nicer alignment in UI (if supported by font).
       */
      tabular?: boolean;
    }
  >;

/**
 * Renders numbers using the dedicated numeric font (Monda via next/font variable `--font-monda`).
 * Useful for percents, counters, scores, timers, etc.
 */
export function NumericText<T extends React.ElementType = "span">(
  props: NumericTextProps<T>
) {
  const {
    as,
    className,
    children,
    value,
    tabular = true,
    style,
    ...rest
  } = props;

  const Component = (as ?? "span") as React.ElementType;

  return (
    <Component
      data-numeric-text
      className={cn(tabular && "tabular-nums", className)}
      style={{
        // More reliable than Tailwind arbitrary font utility in some setups/builds
        fontFamily: "var(--font-monda)",
        ...(style as React.CSSProperties | undefined),
      }}
      {...rest}
    >
      {value ?? children}
    </Component>
  );
}

export default NumericText;